import { z } from 'zod'
import i18n from '@/i18n/config'

// Factory function to get login schema with current translations
export const getLoginSchema = () => z.object({
  email: z
    .string()
    .min(1, i18n.t('validation:email.required'))
    .email(i18n.t('validation:email.invalid')),
  password: z
    .string()
    .min(1, i18n.t('validation:password.required')),
})

// Static schema for backwards compatibility (uses current language at import time)
export const loginSchema = getLoginSchema()

export type LoginFormData = z.infer<typeof loginSchema>

// Factory function to get forgot password schema with current translations
export const getForgotPasswordSchema = () => z.object({
  email: z
    .string()
    .min(1, i18n.t('validation:email.required'))
    .email(i18n.t('validation:email.invalid')),
})

// Static schema for backwards compatibility
export const forgotPasswordSchema = getForgotPasswordSchema()

export type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>

// Factory function to get reset password schema with current translations
export const getResetPasswordSchema = () => z.object({
  password: z
    .string()
    .min(8, i18n.t('validation:password.minLength', { min: 8 }))
    .regex(/[A-Z]/, i18n.t('validation:password.uppercase'))
    .regex(/\d/, i18n.t('validation:password.number')),
  password_confirm: z
    .string()
    .min(1, i18n.t('validation:password.confirmRequired')),
}).refine((data) => data.password === data.password_confirm, {
  message: i18n.t('validation:password.mismatch'),
  path: ['password_confirm'],
})

// Static schema for backwards compatibility
export const resetPasswordSchema = getResetPasswordSchema()

export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>

// 2FA Schemas

// Factory function to get verify 2FA schema with current translations
export const getVerify2FASchema = () => z.object({
  code: z
    .string()
    .length(6, i18n.t('validation:code.length', { length: 6 }))
    .regex(/^\d+$/, i18n.t('validation:code.numbersOnly')),
  rememberDevice: z.boolean().default(false),
})

// Static schema for backwards compatibility
export const verify2FASchema = getVerify2FASchema()

export type Verify2FAFormData = z.infer<typeof verify2FASchema>

// Factory function to get enable 2FA schema with current translations
export const getEnable2FASchema = () => z.object({
  password: z
    .string()
    .min(1, i18n.t('validation:password.required')),
})

// Static schema for backwards compatibility
export const enable2FASchema = getEnable2FASchema()

export type Enable2FAFormData = z.infer<typeof enable2FASchema>

// Profile Schemas

// Factory function to get profile update schema with current translations
export const getProfileUpdateSchema = () => z.object({
  full_name: z
    .string()
    .max(255, i18n.t('validation:name.maxLength', { max: 255 }))
    .optional(),
  avatar_url: z
    .string()
    .url(i18n.t('validation:url.invalid'))
    .max(2048, i18n.t('validation:url.tooLong'))
    .optional()
    .or(z.literal('')),
  language: z
    .enum(['pt-BR', 'es', 'en'])
    .optional(),
  theme: z
    .enum(['dark', 'light', 'system'])
    .optional(),
})

// Static schema for backwards compatibility
export const profileUpdateSchema = getProfileUpdateSchema()

export type ProfileUpdateFormData = z.infer<typeof profileUpdateSchema>

// Factory function to get change password schema with current translations
export const getChangePasswordSchema = () => z.object({
  current_password: z
    .string()
    .min(1, i18n.t('validation:password.currentRequired')),
  new_password: z
    .string()
    .min(8, i18n.t('validation:password.minLength', { min: 8 }))
    .regex(/[A-Z]/, i18n.t('validation:password.uppercase'))
    .regex(/\d/, i18n.t('validation:password.number')),
  new_password_confirm: z
    .string()
    .min(1, i18n.t('validation:password.confirmRequired')),
}).refine((data) => data.new_password === data.new_password_confirm, {
  message: i18n.t('validation:password.mismatch'),
  path: ['new_password_confirm'],
})

// Static schema for backwards compatibility
export const changePasswordSchema = getChangePasswordSchema()

export type ChangePasswordFormData = z.infer<typeof changePasswordSchema>
